import type { Document } from '@/features/bookkeeping/end-of-year-review/types'

/**
 * Options for preserving document fields during deletion in follow-up mode
 */
export interface DocumentDeletionPreserveOptions {
  /** Whether to preserve the name field */
  preserveName?: boolean
  /** Whether to preserve the provider field */
  preserveProvider?: boolean
}

/**
 * Predicate function to find a document for deletion
 */
export type DocumentPredicate = (doc: Document) => boolean

/**
 * Handles document deletion with support for preserving requestReUpload state
 * in follow-up mode. This is the common pattern used across multiple document types.
 *
 * @param documents - The current documents array
 * @param predicate - Function to find the document to delete (e.g., by documentId or categoryCode)
 * @param isFollowUp - Whether we're in follow-up mode
 * @param preserveOptions - Options for which fields to preserve when requestReUpload is true
 * @returns Updated documents array with the document removed or preserved (if requestReUpload)
 *
 * @example
 * // Delete by documentId
 * const updated = handleDocumentDeletion(
 *   documents,
 *   (doc) => doc.documentId === deletedDocumentId,
 *   isFollowUp,
 *   { preserveProvider: true }
 * )
 *
 * @example
 * // Delete by categoryCode
 * const updated = handleDocumentDeletion(
 *   documents,
 *   (doc) => doc.categoryCode === categoryCode,
 *   isFollowUp
 * )
 */
export function handleDocumentDeletion(
  documents: Document[] | undefined,
  predicate: DocumentPredicate,
  isFollowUp: boolean,
  preserveOptions: DocumentDeletionPreserveOptions = {}
): Document[] {
  const allDocuments = documents ?? []
  const deletedDocument = allDocuments.find(predicate)

  // If in follow-up mode and the document had requestReUpload, preserve that state
  // by keeping an entry without documentId so the card shows red state
  if (isFollowUp && deletedDocument?.requestReUpload) {
    const preservedDocument: Document = {
      categoryCode: deletedDocument.categoryCode,
      requestReUpload: true,
      adminNote: deletedDocument.adminNote,
      // No documentId - this indicates the document needs to be re-uploaded
    }

    // Conditionally preserve optional fields based on options
    if (preserveOptions.preserveName && deletedDocument.name) {
      preservedDocument.name = deletedDocument.name
    }

    if (preserveOptions.preserveProvider && deletedDocument.provider) {
      preservedDocument.provider = deletedDocument.provider
    }

    return [...allDocuments.filter((doc) => !predicate(doc)), preservedDocument]
  }

  // Normal deletion - just remove the document
  return allDocuments.filter((doc) => !predicate(doc))
}
